import { useEffect } from 'react';

/**
 * Get the appropriate scroll behavior based on user preferences
 */
const getScrollBehavior = (preferredBehavior: ScrollBehavior = 'smooth'): ScrollBehavior => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return prefersReducedMotion ? 'auto' : preferredBehavior;
};

/**
 * Custom hook to scroll to top when component mounts
 * @param behavior - Scroll behavior ('auto' | 'smooth')
 * @param dependencies - Array of dependencies to trigger scroll effect
 */
export const useScrollToTop = (
  behavior: ScrollBehavior = 'smooth',
  dependencies: any[] = []
) => {
  useEffect(() => {
    const scrollBehavior = getScrollBehavior(behavior);
    window.scrollTo({ top: 0, behavior: scrollBehavior });
  }, dependencies);
};

/**
 * Custom hook to scroll to a specific element by ID
 * @param elementId - The ID of the element to scroll to
 * @param behavior - Scroll behavior ('auto' | 'smooth')
 * @param dependencies - Array of dependencies to trigger scroll effect
 */
export const useScrollToElement = (
  elementId: string,
  behavior: ScrollBehavior = 'smooth',
  dependencies: any[] = []
) => {
  useEffect(() => {
    const element = document.getElementById(elementId);
    if (element) {
      const scrollBehavior = getScrollBehavior(behavior);
      element.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
    }
  }, dependencies);
};

/**
 * Custom hook to scroll to a specific position
 * @param top - The vertical position to scroll to
 * @param left - The horizontal position to scroll to (optional)
 * @param behavior - Scroll behavior ('auto' | 'smooth')
 * @param dependencies - Array of dependencies to trigger scroll effect
 */
export const useScrollToPosition = (
  top: number,
  left: number = 0,
  behavior: ScrollBehavior = 'smooth',
  dependencies: any[] = []
) => {
  useEffect(() => {
    const scrollBehavior = getScrollBehavior(behavior);
    window.scrollTo({ top, left, behavior: scrollBehavior });
  }, dependencies);
};
